import { useState, useEffect, useRef } from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { useConversationMessages } from '../api/getConversationMessages';
import { Spinner } from 'components/Elements';
import { useMessagingSocket } from 'providers/messaging';
import { motion, AnimatePresence } from 'framer-motion';
import { EllipsisVertical } from 'lucide-react'; // icon for 3 dots
import { MessageCircle } from 'react-feather';

export const ChatWindow = ({ conversation }) => {
  const { socket, connected, presence, typingUsers } = useMessagingSocket();
  const { data, isLoading } = useConversationMessages({
    conversationId: conversation?._id,
  });
  const messages = data?.messages || [];

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // ✅ JOIN conversation room when selected
  useEffect(() => {
    if (connected && socket && conversation?._id) {
      socket.emit('join', { conversationIds: [conversation._id] });
      console.log('✅ joined conversation room', conversation._id);
    }
  }, [socket, connected, conversation?._id]);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ Handle background change
  const handleChangeBackground = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      localStorage.setItem('chatBackground', reader.result);
      window.location.reload(); // reload to apply instantly
    };
    reader.readAsDataURL(file);
  };

  // ✅ Handle delete conversation
  const handleDeleteConversation = () => {
    if (!conversation?._id) return;
    if (confirm('Are you sure you want to delete this conversation?')) {
      console.log('Delete conversation:', conversation._id);
      // call API to delete conversation (implement later)
    }
  };

  // ✅ Handle report
  const handleReportConversation = () => {
    console.log('Report conversation:', conversation._id);
    alert('Conversation reported to admin');
  };

  if (!conversation) {
    return (
      <div className="bg-white rounded-xl shadow flex flex-col items-center justify-center text-gray-400 h-full">
        <MessageCircle size={48} className="text-gray-200 mb-4" />
        <span>Select a conversation to start chatting</span>
      </div>
    );
  }

  const typing = typingUsers[conversation._id]?.join(', ');

  return (
    <div className="bg-white rounded-xl shadow flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b p-4 h-20 flex items-center gap-3 flex-shrink-0 relative">
        {/* Avatar */}
        {conversation.otherUser?.avatar ? (
          <img
            src={conversation.otherUser.avatar}
            alt="avatar"
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-semibold text-gray-600">
            {(
              conversation.groupName?.[0] ||
              conversation.otherUser?.name?.[0] ||
              'J'
            ).toUpperCase()}
          </div>
        )}

        {/* User Info */}
        <div className="flex-1">
          <div className="font-semibold text-gray-800">
            {conversation.groupName || conversation.otherUser?.name || 'Conversation'}
          </div>
          <div
            className={
              presence[conversation.otherUser?._id]
                ? 'text-xs text-green-600'
                : 'text-xs text-gray-500'
            }
          >
            {presence[conversation.otherUser?._id] ? 'Online' : 'Offline'}
          </div>
        </div>

        {/* 3-dot menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu((prev) => !prev)}
            className="p-2 rounded-full hover:bg-gray-100 transition-all"
          >
            <EllipsisVertical className="w-5 h-5 text-gray-600" />
          </button>

          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -5 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -5 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-10 w-52 bg-white border shadow-lg rounded-lg py-2 z-50"
              >
                <label className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                  Change background
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleChangeBackground}
                  />
                </label>
                <button
                  onClick={handleDeleteConversation}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Delete conversation
                </button>
                <button
                  onClick={handleReportConversation}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Report conversation
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-b from-blue-50/10 to-transparent">
        {isLoading ? (
          <div className="text-center text-gray-400 mt-10">
            <Spinner />
          </div>
        ) : (
          <MessageList messages={messages} conversationId={conversation._id} />
        )}
      </div>

      {/* Typing indicator */}
      {typing && <div className="text-xs text-gray-400 px-4 pb-1">{typing} is typing...</div>}

      {/* Input (fixed bottom) */}
      <div className="flex-shrink-0">
        <MessageInput conversationId={conversation._id} />
      </div>
    </div>
  );
};
